<script setup lang="ts">
  import { computed, ref, onMounted, onUnmounted } from 'vue'

  import VideoPlayer from '@/components/VideoPlayer.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import CourseDescription from '@/components/course/CourseDescription.vue'
  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import CourseModules from '@/components/course/CourseModules.vue'
  import AutoSlider from '@/components/common/AutoSlider.vue'

  import useCourse from '@/composables/useCourse'
  import useVideo from '@/composables/useVideo'
  import useIcons from '@/composables/useIcons'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useShopifyClient from '@/composables/useShopifyClient'
  import useBreakpoint from '@/composables/useBreakpoint'

  import { UserModule } from '@/store/modules/user'

  const actionsWrapperRef = ref<HTMLElement | null>(null)
  const actionsWrapperHeight = ref(0)

  const { course, courseBundle, lessonsAndDuration, coursePurchasePerksToShow } = useCourse()
  const { getPosterImage } = useVideo()
  const { mediaIcon, teaserImage, arrowRightIndicator, textBlockIndicator } = useIcons()
  const { closeModal, inDevelopment } = useCommonMixin()
  const { createShopifyCheckout } = useShopifyClient()
  const { isSmallerMobileDevice, isDesktopDevice } = useBreakpoint()

  const reviewWidgetClass = computed(() => {
    if (!course.value?.title) return 'elfsight-app-cd80e849-640b-4cbe-991c-02cc8f205d19'

    const courseTitle = course.value.title

    switch (true) {
      case courseTitle.includes('34i'):
        return 'elfsight-app-bd270e0c-e68e-4f3e-b944-0ddb9bc00d44'
      case courseTitle.includes('34d'):
        return 'elfsight-app-103ff473-32e3-4013-b0ad-675fa6155510'
      case courseTitle.includes('34f'):
        return 'elfsight-app-fe2dfdad-d560-41c1-93e1-2ae5503a4a38'
      default:
        return 'elfsight-app-cd80e849-640b-4cbe-991c-02cc8f205d19'
    }
  })

  const buyNow = () => {
    createShopifyCheckout(UserModule.email || undefined, course.value?.shopifyProductVariantId)
  }

  const updateActionsWrapperHeight = () => {
    if (actionsWrapperRef.value) {
      actionsWrapperHeight.value = actionsWrapperRef.value.offsetHeight
    }
  }

  onMounted(() => {
    updateActionsWrapperHeight()
    window.addEventListener('resize', updateActionsWrapperHeight)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', updateActionsWrapperHeight)
  })
</script>

<template lang="pug">
.course-details-modal(:style='{ paddingBottom: `${actionsWrapperHeight}px` }')
  .course-details-overview
    .teaser-details(v-if='course?.teaserVideoUrl || course?.teaserImageUrl')
      VideoPlayer(
        :content-type='"course_teaser_video"',
        :course-id='course?.id',
        :poster-image='getPosterImage(course?.teaserVideoUrl, course?.thumbnailTimestamp || 0)',
        :video-source='course?.teaserVideoUrl',
        v-if='course?.teaserVideoUrl'
      )
      img.teaser-image(:src='course?.teaserImageUrl', v-else-if='course?.teaserImageUrl')
    .course-content-overview
      .content-overview
        .course-short-title
          h5 {{ course?.shortTitle }}
        .course-sector
          h5 {{ $t('courses.ihkExamPreparation') }}
          h2 {{ courseBundle?.sector }}
        .course-content-summary
          SVGRenderer(
            :icon='mediaIcon',
            :stroke-color='"var(--primary-text-color)"',
            :has-hover='false',
            width='30px',
            height='30px'
          )
          h5
            span.content-duration {{ lessonsAndDuration }}
            span {{ $t('contentPreparedByExperts') }}
        .content-separator.ketch-w-c100
        CourseDescription(:description='course?.teaserDescription || ""')
      .teaser-image-wrapper(v-if='isDesktopDevice')
        img(:src='teaserImage')
    CourseDescription(:description='course?.description || ""')
  .content-separator.ketch-w-full
  .google-review-widget-wrapper(v-if='!inDevelopment')
    div(:class='reviewWidgetClass')
  .course-host-info
    .host-image
      img(:src='course?.host?.profileImageUrl')
    .host-info
      .host-name-occupation
        h4 {{ $t('presentedBy', { name: course?.host?.name }) }}
        h5 {{ course?.host?.position }}
      .host-description
        SVGRenderer(
          :icon='textBlockIndicator',
          :stroke-color='"#E6E6EC"',
          :has-hover='false',
          width='20px',
          height='80px'
        )
        h5 {{ course?.hostDescription }}
  CourseModules
  .actions-wrapper.modal-actions(ref='actionsWrapperRef')
    .cta-wrapper
      KetchUpButton.tertiary(@click.native='closeModal')
        h4 {{ $t('previous') }}
      .buy-now-cta(v-if='!course?.purchased && course?.shopifyProductPrice')
        h3.course-price {{ course?.shopifyProductPrice }} €
        KetchUpButton.primary(@click.native='buyNow')
          h4 {{ $t('selectCourse') }}
          SVGRenderer(
            :icon='arrowRightIndicator',
            :fill-color='"var(--primary-foreground-color)"',
            :has-hover='false',
            width='15px',
            height='15px'
          )
    .purchasing-perks(v-if='!course?.purchased')
      AutoSlider(
        :items='coursePurchasePerksToShow',
        :items-per-slide='isSmallerMobileDevice ? 1 : 3',
        :auto-play-interval='3000'
      )
        .perk-item(v-for='(perk, index) in coursePurchasePerksToShow', :key='index')
          SVGRenderer(
            :icon='perk.icon',
            :stroke-color='"var(--primary-text-color)"',
            :has-hover='false',
            width='25px',
            height='25px'
          )
          h5.content-title {{ $t(perk.titleKey, { successRate: course?.successRate }) }}
</template>

<style lang="postcss">
  .course-details-modal {
    .course-details-overview {
      @apply ketch-flex ketch-flex-col ketch-space-y-c20 ketch-px-c20 ketch-pb-c20;

      .teaser-details {
        .video-player {
          @apply ketch-w-full ketch-h-full ketch-max-h-none ketch-rounded-large;

          .plyr {
            @apply ketch-rounded-large;
          }
        }

        img.teaser-image {
          @apply ketch-w-full ketch-h-full ketch-object-cover ketch-rounded-large sm:ketch-max-h-[400px];
        }
      }

      .course-content-overview {
        @apply ketch-flex ketch-flex-col-reverse ketch-border-2 ketch-border-primary-text-color ketch-rounded-large ketch-overflow-hidden;
        @apply xs4:ketch-flex-row;

        .content-overview {
          @apply ketch-flex ketch-flex-col ketch-space-y-c10 ketch-py-c20 ketch-pl-c20;
          @apply sm:ketch-flex-1;

          .course-short-title {
            @apply ketch-px-c10 ketch-py-c5 ketch-rounded-large ketch-w-fit;
            @apply ketch-bg-secondary-color ketch-text-secondary-foreground-color;

            h5 {
              @apply ketch-font-bold;
            }
          }

          .course-sector {
            h2 {
              @apply ketch-font-bold;
            }
          }
        }

        .teaser-image-wrapper {
          img {
            @apply ketch-w-full ketch-h-full ketch-object-cover ketch-max-w-[180px] ketch-max-h-[255px];
          }
        }
      }
    }

    .google-review-widget-wrapper {
      @apply ketch-p-c20;
    }

    .course-host-info {
      @apply ketch-flex ketch-items-center ketch-space-x-c20 ketch-p-c20;

      .host-image {
        @apply ketch-w-[80px] ketch-h-[160px] ketch-rounded-[60px];

        img {
          @apply ketch-w-full ketch-min-w-[80px] ketch-h-full ketch-object-cover ketch-rounded-[60px];
        }
      }

      .host-info {
        @apply ketch-flex ketch-flex-col ketch-space-y-c20;

        .host-name-occupation {
          h4 {
            @apply ketch-font-bold;
          }
        }

        .host-description {
          @apply ketch-flex ketch-items-center ketch-space-x-c10;
        }
      }
    }

    .course-modules-wrapper {
      @apply ketch-px-c20;
    }

    .actions-wrapper {
      @apply ketch-p-c20 ketch-flex ketch-flex-col ketch-space-y-c15 ketch-w-full ketch-z-[2];

      .cta-wrapper {
        @apply ketch-flex ketch-justify-between;

        button {
          @apply ketch-w-fit;
        }

        .buy-now-cta {
          @apply ketch-flex ketch-items-center ketch-space-x-c10;

          .course-price {
            @apply ketch-font-bold;
          }
        }
      }

      .purchasing-perks {
        @apply ketch-relative ketch-overflow-hidden ketch-opacity-80;

        .slider-container {
          @apply xs4:ketch-flex xs4:ketch-items-center xs4:ketch-justify-center xs4:ketch-space-x-c20;

          .perk-item {
            @apply ketch-flex-none ketch-w-full ketch-flex ketch-items-center ketch-justify-center ketch-gap-c5;
            @apply xs4:ketch-w-auto;
          }
        }
      }
    }
  }
</style>
